import React, {useRef, useState} from 'react';
import {
  ACKNOWLEDGMENT_NODE,
  CHALLENGE_BRANCH_AI,
  CHALLENGE_BRANCH_HEADER,
  CHOICE_HEADER_NODE_TYPE,
  IMAGE_NODE_TYPE,
  LessonContentAcknowledgment,
  LessonContentBase,
  LessonContentImageSource,
  LessonContentMChoice,
  LessonContentMChoiceAnswer,
  LessonContentOverlayVideo,
  LessonContentVideo,
  MULTIPLE_CHOICE,
  nodeIsASourceNode,
  OPENENDED_QUESTION_NODE_TYPE,
  OpenEndedQuestionAnswer,
  OpenEndedQuestionContent,
  OVERLAY_VIDEO_TYPE,
  OVERLAY_VIMEO_VIDEO_TYPE,
} from '../../../data/models/LessonContent/LessonContentBase';
import {LessonContentItem} from '../../../data/models/LessonContent/LessonContentList';
import StartingOverlay from '../overlays/StartingOverlay/StartingOverlay';
import EndingOverlay from '../overlays/EndingOverlay/EndingOverlay';
import MCHoiceOverlay from '../overlays/MChoiceOverlay/MChoiceOverlay';
import ChoiceOverlay from '../overlays/ChoiceOverlay/ChoiceOverlay';
import ChallengeBranchOverlay from '../overlays/ChallengeBranchOverlay/ChallengeBranchOverlay';
import ChallengeBranchAIOverlay from '../overlays/ChallengeBranchAIOverlay/ChallengeBranchAIOverlay';
import {GradeResult} from '../models';
import {
  ChallengeBranchAIMapped,
  ChoiceFreeformMapped,
  LessonContentChoiceAnswerMapped,
  LessonContentChoiceMapped,
} from '../../../data/models/LessonContent/LessonConentMapped';
import ResultsModal from '../components/ResultsModal';
import FreeformOverlay from '../overlays/FreeformOverlay/FreeformOverlay';
import './styles.css';
import ChoiceFreeformOverlay from '../overlays/ChoiceFreeformOverlay/ChoiceFreeformOverlay';
import {tapyblPlayerText} from '../tapyblPlayerText';
import AcknowledgmentOverlay from '../overlays/AcknowledgmentOverlay/AcknowledgmentOverlay';
import ImagePlayer from '../components/ImagePlayer';
import PreviewVideoPlayer from '../components/PreviewVideoPlayer';
import VideoPlayer from '../components/VideoPlayer';
import OpenEndedQuestionOverlay from '../overlays/OpenEndedQuestionOverlay/OpenEndedQuestionOverlay';
import AiAssistantOverlayButton from '../components/AiAssistantOverlayButton';

interface Props {
  currentSourseNode?: LessonContentBase;
  started: boolean;
  ended: boolean;
  preview: boolean;
  onFinishedVideo: () => void;
  currentNode?: LessonContentItem;
  isGradable?: boolean;
  onStart: () => void;
  gradeResult?: GradeResult;
  onCourseHome: () => void;
  onContinue: () => void;
  nextLessonIsTheSame?: boolean;
  isLoading: boolean;
  passingScore: number;
  onSelectMChoice: (
    choices: LessonContentMChoiceAnswer[],
    waitForFeedback?: boolean,
  ) => void;
  onContinueMChoice: () => void;
  onSelectChoice: (choice: LessonContentChoiceAnswerMapped) => void;
  onSelectChallenge: (choice: LessonContentChoiceAnswerMapped) => void;
  onSelectFreeformChoice: (choiceId: string) => void;
  onSelectChallengeAI: (
    answer: ChallengeBranchAIMapped,
    selectedAnswer: string,
  ) => void;
  onSelectOpenEndedQuestionAnswer: (answer: OpenEndedQuestionAnswer) => void;
  onAcknowledge: (id: string) => void;
  fitHeight?: boolean;
  skipFinishButtons?: boolean;
  nextLessonId?: string;
  jumpToNode: (nodeId: string) => void;
  navigateTo: (lessonId: string) => void;
  showScormFinish?: boolean;
  organizationId?: string;
  aiAssistantId?: string;
  aiAssistantAvatarUrl?: string;
}

const TapyblPlayerUI = React.memo(
  ({
    currentSourseNode,
    started,
    ended,
    preview,
    onFinishedVideo,
    currentNode,
    isGradable,
    onStart,
    gradeResult,
    onContinue,
    onCourseHome,
    nextLessonIsTheSame,
    isLoading,
    passingScore,
    onSelectChallenge,
    onSelectChallengeAI,
    onSelectFreeformChoice,
    onSelectChoice,
    onSelectMChoice,
    onContinueMChoice,
    onSelectOpenEndedQuestionAnswer,
    fitHeight,
    skipFinishButtons,
    nextLessonId,
    jumpToNode,
    navigateTo,
    showScormFinish,
    onAcknowledge,
    organizationId,
    aiAssistantId,
    aiAssistantAvatarUrl,
  }: Props) => {
    const [showResults, setShowResults] = useState(false);
    const [width, setWidth] = useState(0);
    const [progress, setProgres] = useState(0);
    const [isCssFullscreen, setIsCssFullscreen] = useState(false);
    const playerRef = useRef<any>(null);
    const outerContainerRef = useRef<HTMLDivElement>(null);

    const toggleFullscreen = () => {
      if (!outerContainerRef.current) return;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (isCssFullscreen) {
        setIsCssFullscreen(false);
      } else {
        outerContainerRef.current.requestFullscreen().catch(() => {
          setIsCssFullscreen(true);
        });
      }
    };

    const onPause = () => {
      if (playerRef.current) {
        playerRef.current.pause();
      }
    };

    const seekTo = (second: number) => {
      if (playerRef.current) {
        playerRef.current.seekTo(second, 'seconds');
      }
    };

    const onJumpToNode = (nodeId: string) => {
      onPause();
      jumpToNode(nodeId);
    };

    const onNavigateTo = (lessonId: string) => {
      onPause();
      navigateTo(lessonId);
    };

    const getPlayerForSourceNode = () => {
      if (currentSourseNode?.ivNodeType === IMAGE_NODE_TYPE) {
        const currentImageNode = currentSourseNode as LessonContentImageSource;
        return (
          <ImagePlayer
            imageSource={currentImageNode}
            onFinished={onFinishedVideo}
            started={started}
            onWidthCalculated={setWidth}
            hideControls={
              currentNode?.data.ivNodeType === CHOICE_HEADER_NODE_TYPE ||
              currentNode?.data.ivNodeType === CHALLENGE_BRANCH_HEADER
            }
          />
        );
      } else {
        const currentVideoNode = currentSourseNode as LessonContentVideo;
        if (preview) {
          return (
            <PreviewVideoPlayer
              onFinished={onFinishedVideo}
              currentNode={currentVideoNode}
              started={started}
              showControls={
                preview && nodeIsASourceNode(currentNode?.data.ivNodeType)
              }
              fitHeight={fitHeight}
              onWidthCalculated={setWidth}
              reference={(ref: any) => {
                playerRef.current = ref;
              }}
              setProgres={setProgres}
              hideSmallControls={
                currentNode?.data.ivNodeType === CHOICE_HEADER_NODE_TYPE ||
                currentNode?.data.ivNodeType === CHALLENGE_BRANCH_HEADER
              }
              onFullscreen={toggleFullscreen}
            />
          );
        } else {
          return (
            <VideoPlayer
              onFinished={onFinishedVideo}
              currentNode={currentVideoNode}
              source={currentVideoNode.contentUrl || ''}
              startTime={currentVideoNode.startInSeconds}
              endTime={currentVideoNode.endInSeconds}
              started={started}
              showControls={
                preview && nodeIsASourceNode(currentNode?.data.ivNodeType)
              }
              fitHeight={fitHeight}
              onWidthCalculated={setWidth}
              reference={(ref: any) => {
                playerRef.current = ref;
              }}
              setProgres={setProgres}
              hideSmallControls={
                currentNode?.data.ivNodeType === CHOICE_HEADER_NODE_TYPE ||
                currentNode?.data.ivNodeType === CHALLENGE_BRANCH_HEADER
              }
              onFullscreen={toggleFullscreen}
            />
          );
        }
      }
    };

    return currentSourseNode ? (
      <>
        <div
          className={`TapyblPlayerContainer${
            isCssFullscreen ? ' CssFullscreen' : ''
          }`}
          ref={outerContainerRef}
          style={
            isCssFullscreen
              ? undefined
              : {
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '20px',
                  maxWidth: '100%',
                  maxHeight: '100%',
                }
          }>
          <div className="PlayerVideoWrapper">
            {getPlayerForSourceNode()}
            {organizationId && aiAssistantId && started && !ended && (
              <AiAssistantOverlayButton
                organizationId={organizationId}
                aiAssistantId={aiAssistantId}
                aiAssistantAvatarUrl={aiAssistantAvatarUrl}
              />
            )}
          </div>
          {!started && (
            <div className="PlayerBranchOverlay">
              <StartingOverlay
                gradable={isGradable || false}
                onStart={onStart}
                showResults={() => setShowResults(true)}
                gradeResult={gradeResult}
              />
            </div>
          )}
          {ended && (
            <div className="PlayerBranchOverlay">
              <EndingOverlay
                gradeResult={gradeResult}
                gradable={isGradable}
                onContinue={onContinue}
                onCourseHome={onCourseHome}
                preview={preview}
                isLoading={isLoading}
                nextLessonIsTheSame={nextLessonIsTheSame}
                showResults={() => setShowResults(true)}
                skipFinishButtons={skipFinishButtons}
                nextLessonId={nextLessonId}
                showScormFinish={showScormFinish}
              />
            </div>
          )}
          {currentNode?.data.ivNodeType === MULTIPLE_CHOICE && (
            <div className="PlayerBranchOverlay">
              <MCHoiceOverlay
                onSelectChoice={onSelectMChoice}
                onContinueChoice={onContinueMChoice}
                mChoice={currentNode.data as LessonContentMChoice}
                passingScore={passingScore}
              />
            </div>
          )}
          {(currentNode?.data.ivNodeType === OVERLAY_VIDEO_TYPE ||
            currentNode?.data.ivNodeType === OVERLAY_VIMEO_VIDEO_TYPE) &&
            started && (
              <FreeformOverlay
                overlayNode={currentNode.data as LessonContentOverlayVideo}
                progress={progress}
                width={width}
                onPause={onPause}
                navigateTo={onNavigateTo}
                seekTo={seekTo}
                jumpToNode={onJumpToNode}
                removeDarkening
              />
            )}
          {currentNode?.data.ivNodeType === CHOICE_HEADER_NODE_TYPE && (
            <>
              {(currentNode.data as LessonContentChoiceMapped).isFreeform ? (
                <ChoiceFreeformOverlay
                  width={width}
                  overlayNode={currentNode.data as ChoiceFreeformMapped}
                  onSelectFreeformChoice={onSelectFreeformChoice}
                  passingScore={passingScore}
                />
              ) : (
                <div className="PlayerBranchOverlay">
                  <ChoiceOverlay
                    onSelectChoice={onSelectChoice}
                    choice={currentNode.data as LessonContentChoiceMapped}
                  />
                </div>
              )}
            </>
          )}
          {currentNode?.data.ivNodeType === CHALLENGE_BRANCH_HEADER && (
            <>
              {(currentNode.data as LessonContentChoiceMapped).isFreeform ? (
                <ChoiceFreeformOverlay
                  width={width}
                  overlayNode={currentNode.data as ChoiceFreeformMapped}
                  onSelectFreeformChoice={onSelectFreeformChoice}
                  passingScore={passingScore}
                />
              ) : (
                <div className="PlayerBranchOverlay">
                  <ChallengeBranchOverlay
                    onSelectChoice={onSelectChallenge}
                    choice={currentNode.data as LessonContentChoiceMapped}
                    passingScore={passingScore}
                  />
                </div>
              )}
            </>
          )}
          {currentNode?.data.ivNodeType === CHALLENGE_BRANCH_AI && (
            <ChallengeBranchAIOverlay
              passingScore={passingScore}
              challengeBranch={currentNode.data as ChallengeBranchAIMapped}
              onSelectChallengeAI={onSelectChallengeAI}
            />
          )}
          {currentNode?.data.ivNodeType === ACKNOWLEDGMENT_NODE && (
            <div className="PlayerBranchOverlay">
              <AcknowledgmentOverlay
                acknowledgment={currentNode.data as LessonContentAcknowledgment}
                onAcknowledge={onAcknowledge}
              />
            </div>
          )}
          {currentNode?.data.ivNodeType === OPENENDED_QUESTION_NODE_TYPE && (
            <div className="PlayerBranchOverlay">
              <OpenEndedQuestionOverlay
                onSelectChoice={onSelectOpenEndedQuestionAnswer}
                question={currentNode.data as OpenEndedQuestionContent}
                passingScore={passingScore}
              />
            </div>
          )}
          {showResults && gradeResult && (
            <ResultsModal
              results={gradeResult}
              onCancel={() => setShowResults(false)}
              height={width / 1.77}
            />
          )}
        </div>
      </>
    ) : (
      <div className="LessonIsEmptyContainer">
        <h3>{tapyblPlayerText.lessonIsEmpty}</h3>
      </div>
    );
  },
);

export default TapyblPlayerUI;
